import React, { useState, useRef, useEffect } from 'react';
import { Send } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import './NumberGame.css';

const ANNA_PIC = './anna.png';
const ANON_PIC = './anon.jpg';
const MAX_MESSAGE_LENGTH = 120;

const NumberGame = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(3);
  const [isLoading, setIsLoading] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [userId] = useState(() => {
    const existingId = localStorage.getItem('userId');
    if (existingId) return existingId;
    
    const newId = uuidv4();
    localStorage.setItem('userId', newId);
    return newId;
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const savedState = localStorage.getItem('numberGameState');
    
    if (savedState) {
      const { messages, attemptsLeft, isGameOver } = JSON.parse(savedState);
      setMessages(messages);
      setAttemptsLeft(attemptsLeft);
      setIsGameOver(isGameOver);
    } else {
      setMessages([{
        role: 'assistant',
        content: "Hey, I'm Anna. Need anything?"
      }]);
    }
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem('numberGameState', JSON.stringify({
        messages,
        attemptsLeft,
        isGameOver
      }));
    }
  }, [messages, attemptsLeft, isGameOver]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_MESSAGE_LENGTH) {
      setInputValue(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isGameOver || isLoading) return;
  
    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
  
    try {
      const newMessages = [...messages, { role: 'user', content: userMessage }];
      setMessages(newMessages);
  
      const response = await fetch('https://gethernumber-ece0aa6efd1f.herokuapp.com/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          message: userMessage,
          messages: messages,
          sessionId: userId
        }),
      });
  
      const data = await response.json();
      
      const newAttemptsLeft = attemptsLeft - 1;
      setAttemptsLeft(newAttemptsLeft);
  
      if (newAttemptsLeft === 0) {
        setMessages(prev => [
          ...prev,
          { role: 'assistant', content: data.message },
          { role: 'assistant', content: "Ugh, I gotta go. See ya later." }
        ]);
        setIsGameOver(true);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: data.message }]);
      }
      
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "Sorry, something went wrong. Please try again." 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app-container">
        <a className="powered-by" href="https://lollipop.chat?utm_source=getherdigits.com" target="_blank">
        <img src="./logo.png"></img>
        <div className="powered-by-right">
          <div>Powered by Lollipop</div>
          <div>The most advanced AI girls</div>
        </div>
      </a>
      <div className="content-wrapper">
        <header className="app-header">
          <h1>Get Her Digits</h1>
          <p className="header-subtitle">
            Can you get my number in 3 attempts?
          </p>
          <p className="attempts-counter">Think <b><i>hard</i></b>.</p>
        </header>

        <div className="chat-section">
          <div className="messages-container">
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                {message.role === "assistant" && (
                  <img
                    src={ANNA_PIC}
                    alt="Anna"
                    className="message-profile-pic"
                  />
                )}
                <div className="message-content">{message.content}</div>
                {message.role === "user" && (
                  <img
                    src={ANON_PIC}
                    alt="You"
                    className="message-profile-pic"
                  />
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSubmit} className="input-form">
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              maxLength={MAX_MESSAGE_LENGTH}
              disabled={isGameOver || isLoading}
              placeholder={isGameOver ? "She's gone." : "Type your message..."}
              className="message-input"
            />
            <button
              type="submit"
              disabled={isGameOver || isLoading}
              className="send-button"
            >
              <Send size={20} />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NumberGame;